import { useEffect, useMemo, useRef } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import BoardMesh from './BoardMesh'
import SceneEnvironment from './SceneEnvironment'
import { BOARD_TOP_Y, boardToXZ, keepContextAlive } from './constants'
import { createBoardTextures } from './textures'
import type { BoardTextures } from './textures'
import { advanceLandingGame, initLandingGame, PLAYER_COLOR } from './landingGame'

const MARBLE_RADIUS = 0.56
const MARBLE_Y = BOARD_TOP_Y + MARBLE_RADIUS * 0.9

// A live game of Chinese checkers played by a fast greedy on the landing board:
// each player advances a marble toward its opposite home (preferring jumps), all
// rendered as one instanced mesh per colour. When a game runs long it fades out
// and starts fresh, so the loop never ends.
function LandingGameMarbles({ count, seed }: { count: 2 | 3 | 6; seed: number }) {
  const game = useMemo(() => initLandingGame(count, seed), [count, seed])
  const meshes = useRef<Record<number, THREE.InstancedMesh | null>>({})
  const dummy = useMemo(() => new THREE.Object3D(), [])

  useFrame(({ clock }) => {
    advanceLandingGame(game, clock.elapsedTime)
    for (const player of game.players) {
      const mesh = meshes.current[player]
      if (!mesh) continue
      const pieces = game.byPlayer.get(player)!
      for (let i = 0; i < pieces.length; i += 1) {
        const piece = pieces[i]
        let x: number
        let z: number
        let hop: number
        if (game.anim && game.anim.piece === piece) {
          x = game.anim.x
          z = game.anim.z
          hop = game.anim.hop
        } else {
          const w = boardToXZ(piece.position)
          x = w.x
          z = w.z
          hop = 0
        }
        dummy.position.set(x, MARBLE_Y + hop, z)
        dummy.scale.setScalar(MARBLE_RADIUS)
        dummy.updateMatrix()
        mesh.setMatrixAt(i, dummy.matrix)
      }
      mesh.instanceMatrix.needsUpdate = true
    }
  })

  return (
    <>
      {game.players.map((player) => (
        <instancedMesh
          key={player}
          ref={(el) => {
            meshes.current[player] = el
          }}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          args={[undefined as any, undefined as any, game.byPlayer.get(player)!.length]}
          frustumCulled={false}
        >
          <sphereGeometry args={[1, 22, 16]} />
          <meshPhysicalMaterial
            color={PLAYER_COLOR[player]}
            roughness={0.13}
            metalness={0}
            clearcoat={1}
            clearcoatRoughness={0.06}
            emissive={PLAYER_COLOR[player]}
            emissiveIntensity={0.06}
            envMapIntensity={1.1}
          />
        </instancedMesh>
      ))}
    </>
  )
}

interface LandingBoardProps {
  textures: BoardTextures
  players: 2 | 3 | 6
  seed: number
  position: [number, number, number]
  scale: number
  rotationY: number
}

function LandingBoard({ textures, players, seed, position, scale, rotationY }: LandingBoardProps) {
  return (
    <group position={position} scale={scale} rotation={[0, rotationY, 0]}>
      <BoardMesh textures={textures} simplified />
      <LandingGameMarbles count={players} seed={seed} />
    </group>
  )
}

function Rig() {
  const camera = useThree((state) => state.camera)
  useEffect(() => {
    camera.lookAt(0, 0, -8)
  }, [camera])
  return null
}

function Boards() {
  const textures = useMemo(() => createBoardTextures({ bakedHoles: false }), [])
  useEffect(() => () => Object.values(textures).forEach((texture) => texture.dispose()), [textures])

  // The boards are held static on purpose: moving them makes the fine engraved
  // board texture shimmer/moiré into radial streaks at this grazing angle. The
  // marbles playing out live games provide all the motion the scene needs.
  return (
    <group>
      {/* Front — full six-player game */}
      <LandingBoard textures={textures} players={6} seed={11} position={[0, 0, 4]} scale={1} rotationY={0.12} />
      {/* Middle — three players */}
      <LandingBoard textures={textures} players={3} seed={211} position={[12, 1.5, -16]} scale={0.78} rotationY={-0.32} />
      {/* Back — two players, receding into the haze */}
      <LandingBoard textures={textures} players={2} seed={421} position={[-11, 3.4, -30]} scale={0.6} rotationY={0.36} />
    </group>
  )
}

export default function LandingScene() {
  return (
    <div className="landing-canvas-wrap">
      <Canvas
        camera={{ position: [0, 42, 60], fov: 30, near: 1, far: 220 }}
        gl={{ antialias: true }}
        dpr={[1, 2]}
        onCreated={({ gl }) => keepContextAlive(gl)}
      >
        <color attach="background" args={['#dcc7a0']} />
        <fog attach="fog" args={['#dcc7a0', 62, 118]} />
        <Rig />
        <SceneEnvironment />

        <ambientLight intensity={0.55} color="#fff1d6" />
        <hemisphereLight intensity={0.6} color="#fff0d0" groundColor="#5c3a20" />
        <directionalLight position={[-16, 30, 22]} intensity={1.4} color="#ffe6bc" />
        <directionalLight position={[14, 12, -12]} intensity={0.3} color="#bcd6ff" />

        <Boards />
      </Canvas>
    </div>
  )
}
